import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  TextField, 
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Fade,
  Collapse
} from '@mui/material';
import Grid from '@mui/material/Grid'
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '../store/slices/authSlice';
import PageHeader from '../components/common/PageHeader';
import ProfileCard from '../components/auth/ProfileCard';
import TwoFactorSetup from '../components/auth/TwoFactorSetup';
import { AuthService } from '../services/auth.service';
import { AppDispatch } from '../store';
import { getProfile } from '../store/slices/authSlice';

// Схема валидации для изменения пароля
const passwordSchema = yup.object({
  currentPassword: yup.string().required('Текущий пароль обязателен'),
  newPassword: yup.string()
    .min(8, 'Пароль должен содержать минимум 8 символов')
    .required('Новый пароль обязателен'),
  confirmNewPassword: yup.string()
    .oneOf([yup.ref('newPassword')], 'Пароли должны совпадать')
    .required('Подтверждение пароля обязательно'),
}).required();

interface PasswordFormInputs {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export default function Profile() {
  const user = useSelector(selectUser);
  const dispatch = useDispatch<AppDispatch>();
  const [open2FA, setOpen2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Использование react-hook-form для управления формой изменения пароля
  const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<PasswordFormInputs>({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    }
  });

  const newPassword = watch('newPassword');
  
  // Проверка силы пароля
  const getPasswordStrength = (password: string) => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    return strength;
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const getStrengthColor = () => {
    if (passwordStrength >= 75) return '#4caf50';
    if (passwordStrength >= 50) return '#ff9800';
    return '#f44336';
  };
  
  // Обработчик отправки формы изменения пароля
  const onSubmit = async (data: PasswordFormInputs) => {
    try {
      setLoading(true);
      setError(null);
      
      // Здесь должен быть запрос к API для изменения пароля
      // Примерная реализация (замените на реальный вызов API)
      /*
      await AuthService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      */
      
      // Симуляция для демонстрации
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccess('Пароль успешно изменен');
      reset();
      
      // Обновляем информацию о пользователе
      dispatch(getProfile());
      
      // Скрыть сообщение через 5 секунд
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка изменения пароля');
    } finally {
      setLoading(false);
    }
  };
  
  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <PageHeader 
        title="Профиль пользователя"
        subtitle="Управление профилем и настройками безопасности"
      />
      
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <ProfileCard />
        </Grid>
        
        <Grid size={{ xs: 12, md: 8 }}>
          <Fade in timeout={800}>
            <Paper 
              sx={{ 
                p: 4,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <VpnKeyIcon sx={{ fontSize: 30, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Изменение пароля
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Убедитесь, что ваш пароль надежный и уникальный
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Collapse in={!!error}>
                <Alert 
                  severity="error" 
                  sx={{ mb: 2, borderRadius: 2 }}
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              </Collapse>
              
              <Collapse in={!!success}>
                <Alert 
                  severity="success" 
                  icon={<CheckIcon />}
                  sx={{ mb: 2, borderRadius: 2 }}
                  onClose={() => setSuccess(null)}
                >
                  {success}
                </Alert>
              </Collapse>
              
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="currentPassword"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Текущий пароль"
                          type={showCurrentPassword ? 'text' : 'password'}
                          fullWidth
                          error={!!errors.currentPassword}
                          helperText={errors.currentPassword?.message}
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon sx={{ color: 'action.active' }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                  edge="end"
                                >
                                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="newPassword"
                      control={control}
                      render={({ field }) => (
                        <Box>
                          <TextField
                            {...field}
                            label="Новый пароль"
                            type={showNewPassword ? 'text' : 'password'}
                            fullWidth
                            error={!!errors.newPassword}
                            helperText={errors.newPassword?.message}
                            disabled={loading}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <VpnKeyIcon sx={{ color: 'action.active' }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    edge="end"
                                  >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                          {field.value && (
                            <Box sx={{ mt: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Надежность пароля:
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontWeight: 700,
                                    color: getStrengthColor()
                                  }}
                                >
                                  {passwordStrength >= 75 ? 'Отличная' : 
                                   passwordStrength >= 50 ? 'Хорошая' : 'Слабая'}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  height: 6,
                                  bgcolor: 'rgba(0,0,0,0.1)',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: `${passwordStrength}%`,
                                    bgcolor: getStrengthColor(),
                                    transition: 'all 0.3s',
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="confirmNewPassword"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Подтверждение нового пароля"
                          type={showConfirmPassword ? 'text' : 'password'}
                          fullWidth
                          error={!!errors.confirmNewPassword}
                          helperText={errors.confirmNewPassword?.message}
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <VpnKeyIcon sx={{ color: 'action.active' }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                >
                                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{
                        mt: 1,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                          boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                        },
                        '&:disabled': {
                          background: 'rgba(0,0,0,0.12)',
                        }
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                      ) : (
                        'Изменить пароль'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Fade>
        </Grid>
      </Grid>
      
      <TwoFactorSetup 
        open={open2FA} 
        onClose={() => setOpen2FA(false)} 
        isEnabled={user.isTwoFactorEnabled} 
      />
    </Box>
  );
}